import React, { useContext, useState } from 'react';
import { Form, useFormikContext } from 'formik';

import { FormikTextField } from 'features/product/components/utils/fields';
import MaskedInput from 'react-text-mask';
import { MASK_SHORT_DATE, CREDIT_CARD_MASK, MASK_CVC } from 'common/modules/mask-text/masks';
import { FormikCheckboxField } from 'components/fields/FormikTextField';
import CCImage from '../../../../media/images/allowed-creditcards-img.png';
import CCImageNew from '../../../../media/images/new-allowed-creditcards-img.png';
import {
    expirationDateValidator,
    cvcValidator,
    nameValidator,
    cardNumberValidator,
} from 'common/utils/creditCardValidators';
import useFeatureFlags, { FeatureFlagTypes } from 'common/hooks/useFeatureFlags';
import { Checkbox } from 'components/checkbox/Checkbox';
import { zipCodeValidator, postalCodeValidator } from 'common/modules/form-validation/validators';
import { IS_FLIPGIVE } from 'common/env';
import ResponsiveContainer, {
    ContainerType,
} from 'components/responsive-containers/ResponsiveContainer';
import { validateCardBinWithTokenX } from 'common/api/e-comm/credit-cards/service';
import useMediaQueries from 'hooks/useMediaQueries';
import { useSelector } from 'react-redux';
import { selectIsCardLinkingThroughCheckoutWebEnabled } from 'common/features/store/duck/ffs';
import { useModalControls } from 'hooks/useModalControls';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { CardLinkingStatusAndButton, CardLinkingTermsModal } from './LocalDiningLinking';
import { TOKENEX_ID, TOKENEX_URL } from 'config/env';

export interface CreditCardFields {
    name: string;
    cardNumber: string;
    expirationDate: string;
    securityCode: string;
    zipCode: string;
    save: boolean;
}

export interface CreditDebitCardFormProps {
    makeThisTheDefaultPayment?: boolean;
    setMakeThisTheDefaultPayment?: (state: boolean) => void;
    checkboxText?: string;
    isDebitCard?: boolean;
    isCanada?: boolean;
}

export const CreditDebitCardForm = ({
    makeThisTheDefaultPayment,
    setMakeThisTheDefaultPayment,
    checkboxText = 'Save this card',
    isDebitCard = false,
    isCanada,
}: CreditDebitCardFormProps) => {
    const isPostalCode = isCanada ?? IS_FLIPGIVE;
    const [disabledDefault, setDisabledDefault] = useState(false);
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const formikProps = useFormikContext<CreditCardFields>();

    // Using onChange instead of MaskedInput because react-text-mask has a bug where backspace doesn't work over literal space characters in the mask.
    const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const pattern = [/[A-Z]/, /\d/, /[A-Z]/, /\d/, /[A-Z]/, /\d/];
        let result = '';
        for (let i = 0; i < raw.length && i < 6; i++) {
            if (pattern[i].test(raw[i])) {
                result += raw[i];
            } else {
                break;
            }
        }
        e.target.value = result.length > 3 ? result.slice(0, 3) + ' ' + result.slice(3) : result;
    };

    const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
    };
    const isLocalDiningCheckoutEnabled = useSelector(selectIsCardLinkingThroughCheckoutWebEnabled);
    const cloTermsAndConditionsModal = useModalControls();
    const { cartInfo, setCartInfo } = useContext(OrderScreenContext);

    useMediaQueries({
        '(max-width: 640px)': () => {
            setIsMobile(true);
        },
        '(min-width: 640px)': () => {
            setIsMobile(false);
        },
    });

    const validateDebitCardNumber = async (cardNumber: string): Promise<void> => {
        if (isDebitCard && cardNumber) {
            const validation = await validateCardBinWithTokenX(cardNumber, TOKENEX_ID, TOKENEX_URL);

            if (validation && validation.data) {
                const isDebit = validation.data.isDebit;
                if (!isDebit) {
                    formikProps.setFieldError(
                        'cardNumber',
                        'This number is linked to a Credit Card.'
                    );
                }
            }
        }
    };

    const combinedMaskedCreditDebitMaskedValidator = (value: string) => {
        return cardNumberValidator(value.replace(/\s/g, ''));
    };

    // CASE: user unchecks the link w/ CLO checkbox on the form
    const unEnrollFromRewardsNetwork = () => {
        setCartInfo((cartInfo_) => ({
            ...cartInfo_,
            linkNewCardWithClo: false,
        }));
    };

    const linkWithCLO = () => {
        setCartInfo({
            ...cartInfo,
            linkNewCardWithClo: true,
        });
    };

    return (
        <div data-recording-disable className="ml-0 sm:ml-7">
            <Form>
                <AcceptedCreditCardsDisclaimer />
                <>
                    <div className="w-full">
                        <FormikTextField
                            name="name"
                            label="Name on Card"
                            placeholder="Name on Card"
                            validate={nameValidator}
                        />
                    </div>
                    <br />
                    <div className="flex justify-between gap-2 sm:gap-5">
                        <div className="w-full">
                            <MaskedInput
                                mask={CREDIT_CARD_MASK}
                                guide={false}
                                render={(ref, _props) => (
                                    <FormikTextField
                                        inputRef={ref}
                                        id="cardNumber"
                                        name="cardNumber"
                                        label="Card Number"
                                        placeholder="0000 0000 0000 0000"
                                        validate={combinedMaskedCreditDebitMaskedValidator}
                                        {..._props}
                                        onBlur={(e) => {
                                            const { value } = e.target;
                                            const validationErrors = cardNumberValidator(value);
                                            if (!validationErrors) {
                                                validateDebitCardNumber(value);
                                            }
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <br />
                    <div className="flex justify-between gap-5">
                        <div className="w-full">
                            <MaskedInput
                                mask={MASK_SHORT_DATE}
                                guide={false}
                                render={(ref, _props) => (
                                    <FormikTextField
                                        inputRef={ref}
                                        label="Exp Date"
                                        name="expirationDate"
                                        placeholder="mm/yy"
                                        validate={expirationDateValidator}
                                        {..._props}
                                    />
                                )}
                            />
                        </div>
                        <div className="w-full">
                            <MaskedInput
                                mask={MASK_CVC}
                                guide={false}
                                render={(ref, _props) => (
                                    <FormikTextField
                                        inputRef={ref}
                                        name="securityCode"
                                        label="CVV"
                                        placeholder="000"
                                        maxLength={3}
                                        validate={cvcValidator}
                                        {..._props}
                                    />
                                )}
                            />
                        </div>
                        <ResponsiveContainer For={ContainerType.desktop} defaultVisibility>
                            <div className="w-full">
                                {isMobile ? null : isPostalCode ? (
                                    <FormikTextField
                                        label="Postal Code"
                                        name="zipCode"
                                        placeholder="Ex: A1A 1A1"
                                        validate={postalCodeValidator}
                                        maxLength={7}
                                        onChange={handlePostalCodeChange}
                                    />
                                ) : (
                                    <FormikTextField
                                        label="Zip Code"
                                        name="zipCode"
                                        placeholder="Enter Zip Code"
                                        validate={zipCodeValidator}
                                        maxLength={5}
                                        onChange={handleZipCodeChange}
                                    />
                                )}
                            </div>
                        </ResponsiveContainer>
                    </div>

                    <ResponsiveContainer For={ContainerType.mobile} defaultVisibility>
                        <div className="w-full mt-5">
                            {isMobile ? (
                                isPostalCode ? (
                                    <FormikTextField
                                        label="Postal Code"
                                        name="zipCode"
                                        placeholder="Ex: A1A 1A1"
                                        validate={postalCodeValidator}
                                        maxLength={7}
                                        onChange={handlePostalCodeChange}
                                    />
                                ) : (
                                    <FormikTextField
                                        label="Zip Code"
                                        name="zipCode"
                                        placeholder="Enter Zip Code"
                                        validate={zipCodeValidator}
                                        maxLength={5}
                                        onChange={handleZipCodeChange}
                                    />
                                )
                            ) : null}
                        </div>
                    </ResponsiveContainer>
                    <br />
                </>

                <FormikCheckboxField
                    name="save"
                    id="save"
                    label={checkboxText}
                    className="text-sm pl-1"
                    onStateChange={(val) => {
                        formikProps.setFieldValue('save', val);
                        formikProps.setFieldValue('savePaymentDetails', val);
                        if (!val) {
                            setDisabledDefault(true);
                            setMakeThisTheDefaultPayment && setMakeThisTheDefaultPayment(false);
                        } else {
                            setDisabledDefault(false);
                        }
                    }}
                    checked={formikProps.values.save}
                />
                {makeThisTheDefaultPayment !== undefined && setMakeThisTheDefaultPayment ? (
                    <Checkbox
                        checked={makeThisTheDefaultPayment}
                        onChange={setMakeThisTheDefaultPayment}
                        id="chkDefaultPayment"
                        text="Save as default payment method"
                        labelClassName="pl-1 text-sm"
                        className="mt-1"
                        disabled={disabledDefault}
                    />
                ) : null}
                {isLocalDiningCheckoutEnabled && formikProps.values.save ? (
                    <CardLinkingStatusAndButton
                        isCheckboxDisabled={false}
                        unEnrollFromRewardsNetwork={unEnrollFromRewardsNetwork}
                        enrolledInRewardsNetwork={Boolean(cartInfo.linkNewCardWithClo)}
                        onClick={cloTermsAndConditionsModal.toggleModalVisibility}
                    />
                ) : null}
            </Form>
            <CardLinkingTermsModal
                isVisible={cloTermsAndConditionsModal.isVisible}
                onClose={cloTermsAndConditionsModal.toggleModalVisibility}
                onLinkACard={linkWithCLO}
            />
        </div>
    );
};

const AcceptedCreditCardsDisclaimer = () => {
    const { isEnabled } = useFeatureFlags();
    return (
        <div className="my-4">
            <p className="text-center font-semibold text-xs text-grey-1">We Accept</p>
            {isEnabled(FeatureFlagTypes.enable_debit_web) ? (
                <img width={145} className="mx-auto py-2" src={CCImageNew} />
            ) : (
                <img className="mx-auto" src={CCImage} />
            )}
        </div>
    );
};
